import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  BarChart3,
  // PieChart,
  TrendingUp,
  Settings,
  Eye,
  EyeOff,
  Pin,
  Copy,
  Trash2,
  DollarSign,
  AlertTriangle,
} from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import RentByBedChart from './RentByBedChart';
import RentVsSqftChart from './RentVsSqftChart';
import DemographicsCard from './DemographicsCard';
import IncomeCard from './IncomeCard';
import ExpansionCard from './ExpansionCard';
import CrimeCard from './CrimeCard';
import GenericSectionCard from './GenericSectionCard';

const KNOWN_SECTION_KEYS = ['demographics', 'income', 'expansion', 'crime', 'schools'];

function parseTemplateSectionId(cardId, defaultTemplateId) {
  if (!cardId || typeof cardId !== 'string') return null;
  const legacyMatch = cardId.match(/^(demographics|income|expansion|crime|schools)-(map|data)$/);
  if (legacyMatch) {
    return { templateId: defaultTemplateId, sectionKey: legacyMatch[1], type: legacyMatch[2] };
  }
  const match = cardId.match(/^([a-f0-9]{24})-(.+)-(map|data)$/);
  if (match) {
    return { templateId: match[1], sectionKey: match[2], type: match[3] };
  }
  return null;
}

const DataCard = ({
  id,
  pinned,
  data = [],
  onToggleVisibility,
  onTogglePin,
  onDuplicate,
  onRemove,
  sectionDataByKey = null,
  sectionTitleByKey = null,
  defaultTemplateId = null,
  demographicsData = null,
  addressInfo = null,
  demographicsWebmapID = null,
  arcgisToken = null,
  incomeData = null,
  incomeWebmapID = null,
  incomeAddressInfo = null,
  expansionData = null,
  expansionWebmapID = null,
  expansionAddressInfo = null,
  expansionSectionLoading = false,
  crimeData = null,
  crimeWebmapID = null,
  crimeAddressInfo = null,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const processRentByBedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const bedGroups = {};
    data.forEach(building => {
      building.units?.forEach(unit => {
        const beds = unit.bedrooms || 0;
        const rent = unit.rent || 0;
        if (rent > 0) {
          if (!bedGroups[beds]) {
            bedGroups[beds] = [];
          }
          bedGroups[beds].push(rent);
        }
      });
    });

    const bedLabels = {
      0: 'Studio',
      1: '1 BR',
      2: '2 BR',
      3: '3 BR',
      4: '4 BR',
      5: '5 BR',
    };

    return Object.keys(bedGroups)
      .map(Number)
      .sort((a, b) => a - b)
      .map(beds => {
        const rents = bedGroups[beds];
        const sorted = [...rents].sort((a, b) => a - b);
        const median =
          sorted.length > 0
            ? sorted.length % 2 === 0
              ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
              : sorted[Math.floor(sorted.length / 2)]
            : 0;

        return {
          beds,
          label: bedLabels[beds] || `${beds} BR`,
          median: Math.round(median),
          count: rents.length,
        };
      });
  }, [data]);


  const processUnitMixData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const mixCounts = {};
    data.forEach(building => {
      building.units?.forEach(unit => {
        const beds = unit.bedrooms || 0;
        const baths = unit.bathrooms || 0;
        const key = `${beds}BR/${baths}BA`;
        mixCounts[key] = (mixCounts[key] || 0) + 1;
      });
    });

    const colors = [
      '#39585C',
      '#4FA490',
      '#F97316',
      '#EAB308',
      '#EF4444',
      '#8B5CF6',
      '#06B6D4',
      '#84CC16',
    ];

    return Object.entries(mixCounts)
      .map(([name, value], index) => ({
        name,
        value,
        fill: colors[index % colors.length],
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data]);

  const getCardContent = () => {
    switch (id) {
      case 'rent-by-bed':
        return {
          title: 'Rent Overview by Bed',
          icon: <BarChart3 size={20} />,
          metric: 'Median Rent',
          content: <RentByBedChart data={processRentByBedData} />,
        };
      case 'rent-distribution':
        return {
          title: 'Rent Distribution',
          icon: <PieChart size={20} />,
          metric: 'Distribution Analysis',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PieChart
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Histogram + boxplot
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Selection vs benchmark distribution
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'rent-vs-sqft':
        return {
          title: 'Rent vs Square Footage',
          icon: <TrendingUp size={20} />,
          metric: 'Scatter Analysis',
          content: <RentVsSqftChart data={data} />,
        };
      case 'concessions':
        return {
          title: 'Concessions Analysis',
          icon: <TrendingUp size={20} />,
          metric: 'Concession Rate',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <TrendingUp
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Concessions over time
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  % of units with concessions, avg weeks free
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'inventory-pipeline':
        return {
          title: 'Inventory & Pipeline',
          icon: <BarChart3 size={20} />,
          metric: 'Supply Analysis',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <BarChart3
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Existing + pipeline units by year
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Deliveries, under construction, planned
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'unit-mix':
        const hasUnitMixData = processUnitMixData.length > 0;
        return {
          title: 'Unit Mix Analysis',
          icon: <PieChart size={20} />,
          metric: 'Distribution of Unit Types',
          content: hasUnitMixData ? (
            <Box sx={{ height: '300px', width: '100%' }}>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <Pie
                    data={processUnitMixData}
                    cx='50%'
                    cy='50%'
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill='#8884d8'
                    dataKey='value'
                  >
                    {processUnitMixData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `${value} units`,
                      name,
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PieChart
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  No data available
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Search for buildings to see unit mix
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'demographics-map':
        return {
          title: 'Demographics Map',
          icon: <PieChart size={20} />,
          metric: 'Location Analysis',
          content: (
            <DemographicsCard
              type='map'
              addressInfo={addressInfo}
              demographicsData={demographicsData}
              webmapID={demographicsWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'demographics-data':
        return {
          title: 'Demographics Data',
          icon: <PieChart size={20} />,
          metric: 'Population Analysis',
          content: (
            <DemographicsCard
              type='data'
              addressInfo={addressInfo}
              demographicsData={demographicsData}
              webmapID={demographicsWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'income-map':
        return {
          title: 'Income Map',
          icon: <DollarSign size={20} />,
          metric: 'Location Analysis',
          content: (
            <IncomeCard
              type='map'
              addressInfo={incomeAddressInfo}
              incomeData={incomeData}
              webmapID={incomeWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'income-data':
        return {
          title: 'Income Data',
          icon: <DollarSign size={20} />,
          metric: 'Income Analysis',
          content: (
            <IncomeCard
              type='data'
              addressInfo={incomeAddressInfo}
              incomeData={incomeData}
              webmapID={incomeWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'expansion-map':
        return {
          title: 'Expansion Map',
          icon: <TrendingUp size={20} />,
          metric: 'Location Analysis',
          content: (
            <ExpansionCard
              type='map'
              addressInfo={expansionAddressInfo}
              expansionData={expansionData}
              webmapID={expansionWebmapID}
              arcgisToken={arcgisToken}
              expansionLoading={expansionSectionLoading}
            />
          ),
        };
      case 'expansion-data':
        return {
          title: 'Expansion Data',
          icon: <TrendingUp size={20} />,
          metric: 'Population Growth Analysis',
          content: (
            <ExpansionCard
              type='data'
              addressInfo={expansionAddressInfo}
              expansionData={expansionData}
              webmapID={expansionWebmapID}
              arcgisToken={arcgisToken}
              expansionLoading={expansionSectionLoading}
            />
          ),
        };
      case 'crime-map':
        return {
          title: 'Crime Map',
          icon: <AlertTriangle size={20} />,
          metric: 'Location Analysis',
          content: (
            <CrimeCard
              type='map'
              addressInfo={crimeAddressInfo}
              crimeData={crimeData}
              webmapID={crimeWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'crime-data':
        return {
          title: 'Crime Data',
          icon: <AlertTriangle size={20} />,
          metric: 'Safety Analysis',
          content: (
            <CrimeCard
              type='data'
              addressInfo={crimeAddressInfo}
              crimeData={crimeData}
              webmapID={crimeWebmapID}
              arcgisToken={arcgisToken}
            />
          ),
        };
      case 'demographics':
        return {
          title: 'Demographics',
          icon: <PieChart size={20} />,
          metric: 'Population Analysis',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PieChart
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Age, income, household size
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Selection vs benchmark comparison
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'crime':
        return {
          title: 'Crime & Safety',
          icon: <BarChart3 size={20} />,
          metric: 'Safety Score',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <BarChart3
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Crime rates by category
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Violent, property, total incidents
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'comps-table':
        const hasTableData = data && data.length > 0;
        return {
          title: 'Comparable Properties',
          icon: <BarChart3 size={20} />,
          metric: `${data.length} Properties`,
          content: hasTableData ? (
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: '400px',
                '& .MuiTable-root': {
                  fontSize: '0.75rem',
                },
              }}
            >
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Address
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      align='right'
                    >
                      Units
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      align='right'
                    >
                      Avg Rent
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                      align='right'
                    >
                      Distance
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.slice(0, 20).map(building => {
                    const totalRent = building.units?.reduce(
                      (sum, unit) => sum + (unit.rent || 0),
                      0,
                    ) || 0;
                    const unitCount = building.units?.length || 0;
                    const avgRent =
                      unitCount > 0 ? Math.round(totalRent / unitCount) : 0;

                    return (
                      <TableRow key={building.id} hover>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                              }}
                            >
                              {building.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.625rem',
                                color: '#6B7280',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                              }}
                            >
                              {building.address}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align='right' sx={{ fontSize: '0.75rem' }}>
                          {building.totalUnits || unitCount}
                        </TableCell>
                        <TableCell align='right' sx={{ fontSize: '0.75rem' }}>
                          {avgRent > 0 ? `$${avgRent.toLocaleString()}` : '-'}
                        </TableCell>
                        <TableCell align='right' sx={{ fontSize: '0.75rem' }}>
                          {building.distance
                            ? `${building.distance} mi`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <BarChart3
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  No data available
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Search for buildings to see comparable properties
                </Typography>
              </Box>
            </Box>
          ),
        };
      case 'data-quality':
        return {
          title: 'Data Quality',
          icon: <BarChart3 size={20} />,
          metric: 'Coverage Analysis',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <BarChart3
                  size={48}
                  color='#9CA3AF'
                  style={{ marginBottom: '1rem' }}
                />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    marginBottom: '0.25rem',
                  }}
                >
                  Data completeness by field
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  Sample size, coverage %, last updated
                </Typography>
              </Box>
            </Box>
          ),
        };
      default: {
        const parsed = parseTemplateSectionId(id, defaultTemplateId);
        if (
          parsed &&
          sectionDataByKey &&
          defaultTemplateId
        ) {
          const stateKey = `${parsed.templateId}-${parsed.sectionKey}`;
          const sectionState = sectionDataByKey[stateKey] || {};
          const type = parsed.type === 'map' ? 'map' : 'data';
          const sectionTitle =
            (sectionTitleByKey && sectionTitleByKey[stateKey]) ||
            parsed.sectionKey.charAt(0).toUpperCase() +
              parsed.sectionKey.slice(1).replace(/-/g, ' ');

          const sectionLoading = sectionState.loading === true;
          const loaderContent = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: type === 'map' ? { xs: '20rem', sm: '30rem' } : '256px',
                backgroundColor: '#F9FAFB',
                borderRadius: '0.5rem',
                border: '1px solid #E5E7EB',
              }}
            >
              <CircularProgress sx={{ color: '#4FA490' }} />
            </Box>
          );

          if (sectionLoading) {
            return {
              title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
              icon: <BarChart3 size={20} />,
              metric: type === 'map' ? 'Location Analysis' : 'Data blocks',
              content: loaderContent,
            };
          }

          if (KNOWN_SECTION_KEYS.includes(parsed.sectionKey)) {
            const addr = sectionState.addressInfo;
            const webmap = sectionState.webmapID;
            const token = arcgisToken;
            const sectionData = sectionState.data;

            if (parsed.sectionKey === 'demographics') {
              return {
                title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
                icon: <BarChart3 size={20} />,
                metric: type === 'map' ? 'Location Analysis' : 'Population Analysis',
                content: (
                  <DemographicsCard
                    type={type}
                    addressInfo={addr}
                    demographicsData={sectionData}
                    webmapID={webmap}
                    arcgisToken={token}
                  />
                ),
              };
            }
            if (parsed.sectionKey === 'income') {
              return {
                title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
                icon: <DollarSign size={20} />,
                metric: type === 'map' ? 'Location Analysis' : 'Income Analysis',
                content: (
                  <IncomeCard
                    type={type}
                    addressInfo={addr}
                    incomeData={sectionData}
                    webmapID={webmap}
                    arcgisToken={token}
                  />
                ),
              };
            }
            if (parsed.sectionKey === 'expansion') {
              return {
                title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
                icon: <TrendingUp size={20} />,
                metric: type === 'map' ? 'Location Analysis' : 'Population Growth Analysis',
                content: (
                  <ExpansionCard
                    type={type}
                    addressInfo={addr}
                    expansionData={sectionData}
                    webmapID={webmap}
                    arcgisToken={token}
                    expansionLoading={expansionSectionLoading}
                  />
                ),
              };
            }
            if (parsed.sectionKey === 'crime') {
              return {
                title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
                icon: <AlertTriangle size={20} />,
                metric: type === 'map' ? 'Location Analysis' : 'Safety Analysis',
                content: (
                  <CrimeCard
                    type={type}
                    addressInfo={addr}
                    crimeData={sectionData}
                    webmapID={webmap}
                    arcgisToken={token}
                  />
                ),
              };
            }
            if (parsed.sectionKey === 'schools') {
              const rawSchools = sectionState.data?.schools ?? sectionData;
              const schoolsBlocks = Array.isArray(sectionState.data?.blocks) && sectionState.data.blocks.length > 0
                ? sectionState.data.blocks
                : [
                    ...(rawSchools?.district_name != null
                      ? [{ title: 'District', key: 'district_name', value: String(rawSchools.district_name) }]
                      : []),
                    ...(Array.isArray(rawSchools?.schools_nearest_3) && rawSchools.schools_nearest_3.length > 0
                      ? [
                          {
                            title: 'Nearest 3 schools',
                            key: 'schools_nearest_3',
                            value: rawSchools.schools_nearest_3
                              .map((s) => (s.rating ? `${s.name} (${s.rating})` : s.name || ''))
                              .filter(Boolean)
                              .join(', ') || '-',
                          },
                        ]
                      : []),
                  ];
              return {
                title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
                icon: <BarChart3 size={20} />,
                metric: type === 'map' ? 'Location Analysis' : 'School Data',
                content: (
                  <GenericSectionCard
                    type={type}
                    addressInfo={addr}
                    webmapID={webmap}
                    arcgisToken={token}
                    blocks={schoolsBlocks}
                    sectionTitle={sectionTitle}
                  />
                ),
              };
            }
          }

          const blocks = sectionState.data?.blocks ?? (Array.isArray(sectionState.data) ? sectionState.data : []);
          return {
            title: `${sectionTitle} ${type === 'map' ? 'Map' : 'Data'}`,
            icon: <BarChart3 size={20} />,
            metric: type === 'map' ? 'Location Analysis' : 'Data blocks',
            content: (
              <GenericSectionCard
                type={type}
                addressInfo={sectionState.addressInfo}
                webmapID={sectionState.webmapID}
                arcgisToken={arcgisToken}
                blocks={blocks}
                sectionTitle={sectionTitle}
              />
            ),
          };
        }

        return {
          title: 'Custom Card',
          icon: <BarChart3 size={20} />,
          metric: 'Analysis',
          content: (
            <Box
              sx={{
                height: '256px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '0.5rem',
                border: '2px dashed #D1D5DB',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                Card content
              </Typography>
            </Box>
          ),
        };
      }
    }
  };

  const cardContent = getCardContent();

  return (
    <Card
      sx={{
        borderRadius: '0.5rem',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        cursor: 'move',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <Box
        sx={{
          padding: { xs: '0.75rem', md: '1rem' },
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#F9FAFB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: '0.5rem', md: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ color: '#39585C', flexShrink: 0 }}>{cardContent.icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                color: '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cardContent.title}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.625rem', md: '0.75rem' },
                color: '#6B7280',
              }}
            >
              {cardContent.metric}
            </Typography>
          </Box>
          {pinned && (
            <Chip
              label='Pinned'
              size='small'
              sx={{
                height: '20px',
                fontSize: '0.625rem',
                backgroundColor: '#E5E7EB',
              }}
            />
          )}
        </Box>
        <IconButton
          size='small'
          onClick={e => setMenuAnchor(e.currentTarget)}
          sx={{ padding: '0.25rem' }}
        >
          <Settings size={16} />
        </IconButton>
      </Box>
      <Box sx={{ padding: { xs: '0.75rem', md: '1rem' } }}>
        {cardContent.content}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            onToggleVisibility();
            setMenuAnchor(null);
          }}
        >
          {pinned ? (
            <EyeOff size={16} style={{ marginRight: '0.5rem' }} />
          ) : (
            <Eye size={16} style={{ marginRight: '0.5rem' }} />
          )}
          Hide
        </MenuItem>
        <MenuItem
          onClick={() => {
            onTogglePin();
            setMenuAnchor(null);
          }}
        >
          <Pin size={16} style={{ marginRight: '0.5rem' }} />
          {pinned ? 'Unpin' : 'Pin to Top'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDuplicate();
            setMenuAnchor(null);
          }}
        >
          <Copy size={16} style={{ marginRight: '0.5rem' }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            onRemove();
            setMenuAnchor(null);
          }}
          sx={{ color: '#EF4444' }}
        >
          <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
          Remove
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default DataCard;
